import {event_getEventsByProps_Api, event_getUpcomingEventsByProps_Api} from 'apis/event'
import {getId, getType} from 'apis/model/base'
import _ from 'lodash'
import React, {useContext} from 'react'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import OrganizationContainerContext from '../OrganizationContainerContext'
import useTranslate from '../../../../modules/local/useTranslate'
import {SiderBlockHeader} from "../components/SiderBlockHeader";
import {SiderBlockCard} from "../components/SiderBlockCard";
import renderPagingLoading from "../../../MainPage/functions/renderPagingLoading";
import CustomLazyPaginationWrapper from "../../../../modules/asyncCache/components/CustomLazyPaginationWrapper";
import {getEventTypeTitle} from "../../../Event/components/EventOverviewSetting";
import getTitle from "../../../../helpers/getTitle";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import {formatSimplifyDate} from "../../../../helpers/dateUtils";
import {scrollToRow} from "../../Missal/functions/scrollToRow";
import {FiChevronsRight} from "react-icons/fi";
import {useHistory} from "../../../../modules/navigation/useRouter";
import {BsFillCalendar2DateFill, BsPinMapFill} from "react-icons/bs";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import dayjs from "dayjs";
import classNames from "classnames";
import EmptyHolder from "../../../../components/EmptyHolder";
import {Skeleton} from "antd";
import {ItemInfoWithIcon} from "../../../../components/common/ItemInfoWithIcon";

// ─── Status badge ─────────────────────────────────────────────────────────────

const EventStatusBadge = ({time_open, time_close, is_expired}) => {
    const t = useTranslate()
    const now = dayjs()
    const open = time_open ? dayjs.utc(time_open).local() : null
    const close = time_close ? dayjs.utc(time_close).local() : null

    if (is_expired || (close && now.isAfter(close))) {
        return (
            <span className="px-2 py-0.5 text-xs font-semibold text-color-300 bg-color-50 rounded-full border border-color-100">
                {t('closed')}
            </span>
        )
    }
    if (open && now.isBefore(open)) {
        return (
            <span className="px-2 py-0.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-full border border-blue-200">
                {t('upcoming')}
            </span>
        )
    }
    return (
        <span className="px-2 py-0.5 text-xs font-semibold text-green-600 bg-green-50 rounded-full border border-green-200">
            {t('ongoing')}
        </span>
    )


}

// ─── Date block ───────────────────────────────────────────────────────────────

const EventDateBlock = ({time_open, time_close, is_expired}) => {
    const {isLg} = useContext(LayoutContext)
    const d = dayjs.utc(time_open).local()
    const now = dayjs()
    const open = time_open ? dayjs.utc(time_open).local() : null
    const close = time_close ? dayjs.utc(time_close).local() : null

    const isPast = is_expired || (close && now.isAfter(close))
    const isUpcoming = open && now.isBefore(open)

    const color = isPast
        ? {
            bgColor: 'bg-gray-100',
            textColor: 'text-gray-600',
            borderColor: 'bg-gray-300',
        }
        : isUpcoming
            ? {
                bgColor: 'bg-blue-50',
                textColor: 'text-blue-600',
                borderColor: 'bg-blue-600',
            } : {
                bgColor: 'bg-green-50',
                textColor: 'text-green-600',
                borderColor: 'bg-green-500',
            }

    if (!time_open) return null

    return (
        <div className={classNames('p-3 w-20 md:w-28 h-28 flex flex-col items-center flex-shrink-0 rounded-2xl', color?.bgColor)}>
            <span className="font-semibold text-xs text-slate-400 uppercase tracking-wide">
                {d.format(isLg ? 'MMMM' : 'MMM')}
            </span>
            <span className={classNames('font-mono font-bold text-3xl leading-none', color?.textColor)}>
                {d.format('DD')}
            </span>
            <span className="font-medium text-xs text-color-400 tracking-wider">{d.format('YYYY')}</span>
            <div className={classNames("mt-2 w-12 md:w-16 h-1 rounded-full", color?.borderColor)} />
        </div>
    )
}

// ─── Event card ───────────────────────────────────────────────────────────────

const EventCard = ({item, t}) => {
    const {title, time_open, time_close, location_name, event_type, is_expired} = item ?? {}
    const typeTitle = getEventTypeTitle(event_type)

    return (
        <div
            className={classNames(
                "p-3 md:p-4 flex gap-3 background border border-color-50 rounded-2xl shadow-sm hover:shadow-md hover:scale-3xs overflow-hidden transition-all duration-300",
                is_expired && "opacity-50 hover:opacity-80"
            )}>
            <EventDateBlock time_open={time_open} time_close={time_close} is_expired={is_expired}/>
            <div className="flex flex-col flex-1 min-w-0 gap-1">
                <Link
                    to={getLinkToDetail(item)}
                    className="font-bold text-base leading-snug max-lines-2 no-underline">
                    {getTitle(item) || title}
                </Link>
                <div className="flex items-start justify-between gap-2">
                    {typeTitle ? (
                        <span className="text-xs text-indigo-500 italic font-medium tracking-wide">
                            {t(typeTitle)}
                        </span>
                    ) : <div className="flex-1"/>}
                    <div className="flex-shrink-0 mt-0.5">
                        <EventStatusBadge time_open={time_open} time_close={time_close} is_expired={is_expired}/>
                    </div>
                </div>
                {time_close && (
                    <div className="flex items-center gap-1 ">
                        <BsFillCalendar2DateFill size={11} className="text-green-500 flex-shrink-0"/>
                        <span className="font-semibold text-xs text-color-100 tracking-wider">
                            <span className="font-normal text-color-300">
                                {`${_.capitalize(t('to date'))}: `}
                            </span>
                            {formatSimplifyDate(time_close, 'medium')}
                        </span>
                    </div>
                )}
                {location_name && (
                    <div className="flex items-center gap-1 text-xs text-color-300">
                        <BsPinMapFill size={11} className="text-blue-400 flex-shrink-0"/>
                        <span className="font-semibold text-xs text-color-100 tracking-wider max-lines-1">
                            <span className="font-normal text-color-300">
                                {`${_.capitalize(t('locations'))}: `}
                            </span>
                            {location_name}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const EventSkeleton = () => (
    <div className="flex flex-col gap-4">
        {[1, 2, 3].map(i => (
            <div key={i} className="flex background border border-color-50 rounded-xl overflow-hidden">
                <div className="w-16 flex-shrink-0 background-100"/>
                <div className="flex-1 px-4 py-3">
                    <Skeleton active title={{width: '60%'}} paragraph={{rows: 2}}/>
                </div>
            </div>
        ))}
    </div>
)

// ─── Sidebar upcoming events ──────────────────────────────────────────────────

export const OrganizationUpcomingEventBlock = ({}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const history = useHistory()
    const {organization: item} = useContext(OrganizationContainerContext)
    const [id, type] = [getId(item), getType(item)]

    if (_.isEmpty(item)) return null

    return (
        <CustomLazyPaginationWrapper
            sample={2}
            apiInfo={event_getUpcomingEventsByProps_Api}
            renderLoading={renderPagingLoading}
            query={bindQueryParams([{id}, {type}])}
            // Component này render ở cả sidebar desktop (khung thẻ riêng, SiderBlockCard) VÀ dòng nội
            // dung chính trên mobile (OrganizationInfo.js, isLg=false - hoà vào trang, không cần
            // khung/bóng đổ riêng) - chọn Wrapper theo isLg như OrganizationChurchOrAnnouncement.
            renderWrap={(children, __, count) => {
                if (!count) return false
                return (
                    <SiderBlockCard >
                        <SiderBlockHeader
                            icon={BsFillCalendar2DateFill}
                            iconBgClassName="bg-green-50"
                            iconColorClassName="text-green-500"
                            title={t('events')}
                        />
                        {children}
                    </SiderBlockCard>
                )
            }}
            renderItem={(e, i) => {
                const getStatus = (time_open, time_close) => {
                    const now = dayjs()
                    const open = dayjs.utc(time_open).local()
                    const close = dayjs.utc(time_close).local()
                    if (now.isBefore(open)) return <span className="font-medium text-green-600 italic">{t('upcoming')}</span>
                    if (now.isAfter(close)) return <span className="font-medium text-primary-500 italic">{t('closed')}</span>
                    return <span className="font-medium text-blue-600 italic">{t('ongoing')}</span>
                }
                return (
                    <div key={i} className="px-3 py-2 background-50 hover:scale-2xs hover:shadow-md rounded-xl">
                        <div className="mb-2 pb-2 border-b flex flex-col">
                            <Link to={getLinkToDetail(e)}
                                  className="font-bold text-base text-color-000 leading-snug max-lines-1 no-underline">
                                {getTitle(e)}
                            </Link>
                            <span className="text-xs text-indigo-400 italic leading-snug tracking-wider">
                                {t(getEventTypeTitle(e?.event_type))}
                            </span>
                        </div>
                        <div className="space-x-1">
                            <BsFillCalendar2DateFill size={12} className="mx-1 text-green-500"/>
                            <span className="text-xs text-gray-700 tracking-wide">
                                {formatSimplifyDate(e?.time_open, 'fullTime')}
                                {' - '}{getStatus(e?.time_open, e?.time_close)}
                            </span>
                        </div>
                        <ItemInfoWithIcon
                            icon={<BsPinMapFill size={12} className="mx-1 text-green-500"/>}
                            value={e?.location_name}
                            className="text-xs italic tracking-wide"
                        />
                    </div>
                )
            }}
            // Trước đây hiện "Xem thêm" VÔ ĐIỀU KIỆN (bỏ qua hẳn nextUrl/asyncData truyền vào) - hiện
            // cả khi tổ chức chỉ có đúng 1-2 sự kiện, không còn gì để xem thêm.
            renderLoadMore={(onClick, reachEnd, asyncData, nextUrl) => {
                const fetchedCount = asyncData?.response?.data?.data?.length ?? 0
                if (!nextUrl && fetchedCount <= 2) return null
                return (
                    <span
                        className="flex items-center justify-end gap-1 text-color-300 hover:text-color-100 cursor-pointer"
                        onClick={() => {
                            scrollToRow('contentOrg')
                            history.push(getLinkToDetail(item) + '/events')
                        }}>
                        <span className="text-xs tracking-wider">{t('see more')}</span>
                        <FiChevronsRight size={13} className="my-auto"/>
                    </span>
                )
            }}
        />
    )
}

// ─── Main event list ──────────────────────────────────────────────────────────

const OrganizationEventList = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)
    const [id, type] = [getId(item), getType(item)]

    if (_.isEmpty(item)) return null

    return (
        <Wrapper className="flex flex-col gap-6 pb-8">
            <div className="flex flex-col flex-1">
                <div className="font-semibold text-lg text-gray-800 tracking-wide">
                    {t('event')}
                </div>
                <div className="w-20 h-1 my-1 rounded-full bg-gray-400"/>
                <span className="text-color-300 tracking-wider">
                    {t('event introduction description')}
                </span>
            </div>
            <CustomLazyPaginationWrapper
                alwaysVisible={true}
                apiInfo={event_getEventsByProps_Api}
                query={bindQueryParams([{id}, {type}])}
                renderItem={(e) => <EventCard key={getId(e)} item={e} t={t}/>}
                renderLoading={() => <EventSkeleton/>}
                renderWrap={(children, __, count) => {
                    if (!count) return (
                        <EmptyHolder
                            title={t('event')}
                            subTitle={t('No data available')}
                        />
                    )
                    return (
                        <div className="flex flex-col gap-4">
                            {children}
                        </div>
                    )
                }}
            />
            <div className="h-16"/>
        </Wrapper>
    )
}

export default OrganizationEventList
